<template>
  <div class="row items-center">
    <transition-group
      name="list-complete"
      class="row q-gutter-xs items-center"
      tag="span"
    >
      <q-chip
        v-if="visible('draft')"
        key="draft-chip"
        class="list-complete-item"
        size="lg"
        dense
        square
        :outline="orderItem.state !== 'draft'"
        :text-color="orderItem.state === 'draft' ? 'white' : null"
        color="grey-7"
        label="Draft"
        clickable
      >
        <q-tooltip max-width="330px">
          <div class="text-bold text-body2">
            Draft State
          </div>
          <div class="text-body2">
            This Order Item is a placeholder and has not yet been placed. <br><br>
            An Order Item in this state is completely enert, with the exception of a Ticket, which does hold a seat for 5 minutes.
          </div>
          <div
            v-if="orderItem.createdAt"
            class="text-bold text-body2 q-mt-md"
          >
            Entered State
          </div>
          <div
            v-if="orderItem.createdAt"
            class="text-body2"
          >
            <formatted-date
              :value="orderItem.createdAt"
              format="long"
            />
          </div>
        </q-tooltip>
      </q-chip>
      <q-icon
        v-if="internalExpanded"
        key="placed-arrow"
        class="list-complete-item"
        name="arrow_right_alt"
        style="margin: -0px"
        color="grey-8"
        size="md"
      >
        <q-tooltip v-if="orderItem.placedAt">
          {{ orderItem.placedAt.relativeTime({ relativeTo: orderItem.createdAt, duration: true }) }}
        </q-tooltip>
      </q-icon>
      <q-chip
        v-if="visible('placed')"
        key="placed-chip"
        class="list-complete-item"
        :class="{ future: isFuture('placed') }"
        size="lg"
        dense
        square
        :outline="orderItem.state !== 'placed'"
        :text-color="orderItem.state === 'placed' ? 'white' : null"
        color="green-7"
        label="Placed"
        clickable
      >
        <q-tooltip max-width="330px">
          <div class="text-bold text-body2">
            Placed State
          </div>
          <div class="text-body2">
            An item that has been placed is effective. This triggers all the following:
            <br><br>
            For a Ticket
            <ul class="q-mt-none">
              <li>Reserves the Seat</li>
              <li>Creates the unique Ticket Number for Box Office Reporting</li>
            </ul>
            For an Item
            <ul class="q-mt-none">
              <li>Deducts Item From Inventory</li>
              <li>Sends Item to Kitchen Display and Prints at Prep Printers</li>
            </ul>
            Accounting
            <ul class="q-mt-none">
              <li>This item is now considered sold</li>
              <li>Revenue will be earned on effective date</li>
              <li>Until Paid (e.g. Open Tab or Invoice), this is added to Accounts Receivable</li>
            </ul>
          </div>
          <div
            v-if="orderItem.placedAt"
            class="text-bold text-body2 q-mt-md"
          >
            Entered State
          </div>
          <div
            v-if="orderItem.placedAt"
            class="text-body2"
          >
            <formatted-date
              :value="orderItem.placedAt"
              format="long"
            />
          </div>
        </q-tooltip>
      </q-chip>
      <q-icon
        v-if="!orderItem.isCanceled && internalExpanded"
        key="paid-arrow"
        class="list-complete-item"
        name="arrow_right_alt"
        style="margin: -0px"
        color="grey-8"
        size="md"
      >
        <q-tooltip v-if="orderItem.placedAt && orderItem.paidAt">
          {{ orderItem.paidAt.relativeTime({ relativeTo: orderItem.placedAt, duration: true }) }}
        </q-tooltip>
      </q-icon>
      <q-chip
        v-if="!orderItem.isCanceled && visible('paid')"
        key="paid-chip"
        class="list-complete-item"
        :class="{ future: isFuture('paid') }"
        size="lg"
        dense
        square
        :outline="orderItem.state !== 'paid'"
        :text-color="orderItem.state === 'paid' ? 'white' : null"
        color="green-7"
        label="Paid"
        clickable
      >
        <q-tooltip max-width="330px">
          <div class="text-bold text-body2">
            Paid State
          </div>
          <div class="text-body2">
            An Order Item is paid when money has been received for this Order Item.
            <br><br>
            If this is a {{ currentCircuit.localeCurrencySymbol }}0 item, it is considered paid when the Order Item is Placed.
            <br><br>
            A Paid Order Item is always also Placed (or subsequently Voided)
            <br><br>
            Accounting
            <ul class="q-mt-none">
              <li>There will always be a corresponding payment at this same time (unless this is a {{ currentCircuit.localeCurrencySymbol }}0 item)</li>
              <li>There is not necissarily a 1 to 1 correspondent between payments</li>
              <li>The total paid Order Items for the day must equal the total payments for the accounting day or the order is considered malformed</li>
            </ul>
          </div>
          <div
            v-if="orderItem.paidAt"
            class="text-bold text-body2 q-mt-md"
          >
            Entered State
          </div>
          <div
            v-if="orderItem.paidAt"
            class="text-body2"
          >
            <formatted-date
              :value="orderItem.paidAt"
              format="long"
            />
          </div>
        </q-tooltip>
      </q-chip>
      <q-icon
        v-if="internalExpanded"
        key="voided-arrow"
        class="list-complete-item"
        name="arrow_right_alt"
        style="margin: -0px"
        color="grey-8"
        size="md"
      >
        <q-tooltip v-if="orderItem.placedAt && orderItem.voidedAt">
          {{ orderItem.voidedAt.relativeTime({ relativeTo: (orderItem.paidAt || orderItem.placedAt), duration: true }) }}
        </q-tooltip>
      </q-icon>
      <q-chip
        v-if="visible('voided')"
        key="voided-chip"
        class="list-complete-item"
        :class="{ future: isFuture('voided') }"
        size="lg"
        dense
        square
        :outline="orderItem.state !== 'voided'"
        :text-color="orderItem.state === 'voided' ? 'white' : null"
        color="red-7"
        label="Voided"
        clickable
      >
        <q-tooltip max-width="500px">
          <div class="text-bold text-body2">
            Voided State
          </div>
          <div class="text-body2">
            A Void Order Item is no longer effective. It fully reverses the effects of Placing.
            <br><br>
            For a Ticket
            <ul class="q-mt-none">
              <li>Immidiately releases the seat</li>
              <li>Marks the Ticket as Voided and Shows as Void in Box Office Reporting</li>
            </ul>
            For an Item
            <ul class="q-mt-none">
              <li>Adds the item back to Inventory</li>
              <li>Sends Void Notification to Kitchen Display and Prints at Prep Printers</li>
            </ul>
            Points
            <ul class="q-mt-none">
              <li>Any points used will be returned</li>
              <li>Any points earned will be unearned</li>
            </ul>
            Accounting
            <ul class="q-mt-none">
              <li>If the void was prior to or on the Effective Date, No Revenue will be Recogonized on the Effective Date.</li>
              <li>If the void was after the Effective Date, and therefore after the renue was earned, the earned revenue on the date of the void will be reversed.</li>
              <li>There will always be a corresponding Payment at this same time (unless this is a {{ currentCircuit.localeCurrencySymbol }}0 item)</li>
              <li>The total voided Order Items value for the day must equal the total reversed Payments for the accounting day or the order is considered malformed</li>
            </ul>
          </div>
          <div
            v-if="orderItem.voidedAt"
            class="text-bold text-body2 q-mt-md"
          >
            Entered State
          </div>
          <div
            v-if="orderItem.voidedAt"
            class="text-body2"
          >
            <formatted-date
              :value="orderItem.voidedAt"
              format="long"
            />
          </div>
        </q-tooltip>
      </q-chip>
      <q-btn
        v-if="expandable"
        key="expand-btn"
        :icon="internalExpanded ? 'arrow_back' : 'arrow_forward'"
        class="list-complete-item q-ml-sm q-mb-xs"
        round
        dense
        color="grey-6"
        size="xs"
        @click="internalExpanded = !internalExpanded"
      />
    </transition-group>
  </div>
</template>

<script>
import FormattedDate from 'src/modules/admin/components/FormattedDate'

const stateSequence = ['draft', 'placed', 'paid', 'voided']

export default {
  name: 'OrderItemStateMap',
  components: {
    FormattedDate
  },
  props: {
    orderItem: {
      type: Object,
      required: false,
      default: null
    },
    expandable: {
      type: Boolean,
      required: false,
      default: true
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      internalExpanded: this.expanded
    }
  },
  configuration: ['currentCircuit'],
  methods: {
    visible (state) {
      return this.internalExpanded || this.orderItem.state === state
    },
    isFuture (state) {
      const index = stateSequence.indexOf(this.orderItem.state)
      const stateIndex = stateSequence.indexOf(state)

      return index < stateIndex
    }
  }
}

</script>

<style lang="scss" scoped>
.future {
  border: dashed 1px;
  opacity: 0.6;
}

.list-complete-item {
  transition: all 0.5s;
  display: inline-block;
}
.list-complete-enter {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-to {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-active {
  position: absolute;
}

</style>
